import React, { useState, useEffect, useCallback } from 'react';
import { 
  Twitter, RefreshCw, Check, X, Eye, ChevronRight, 
  AlertCircle, Clock, TrendingUp, ExternalLink, 
  FileText, Zap, Plus, Trash2, Edit2, ToggleLeft, ToggleRight,
  Activity, History, CheckSquare, Filter, Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import * as api from '../../services/api';

// ============================================
// X MONITOR MANAGER
// Real-time news monitoring from Twitter/X
// ============================================

const XMonitorManager = () => {
  // State
  const [activeView, setActiveView] = useState('approvals');
  const [approvals, setApprovals] = useState([]);
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedBody, setEditedBody] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Account management state
  const [newAccount, setNewAccount] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    account: 'all',
    dateFrom: '',
    dateTo: '',
    scoreMin: '',
    scoreMax: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [schedulerLoading, setSchedulerLoading] = useState(false);

  // Handler for pause/resume
  const handleToggleMonitor = async () => {
    setSchedulerLoading(true);
    try {
      if (stats?.scheduler?.is_paused) {
        const result = await api.resumeXMonitor();
        console.log('Resume result:', result);
        // Actualizar estado local inmediatamente
        setStats(prev => ({
          ...prev,
          scheduler: {
            ...prev?.scheduler,
            is_paused: false,
            interval_minutes: result.interval || prev?.scheduler?.interval_minutes
          }
        }));
      } else {
        const result = await api.pauseXMonitor();
        console.log('Pause result:', result);
        // Actualizar estado local inmediatamente
        setStats(prev => ({
          ...prev,
          scheduler: {
            ...prev?.scheduler,
            is_paused: true,
            next_run: null
          }
        }));
      }
      // Refresh data después de un pequeño delay
      setTimeout(() => fetchData(), 500);
    } catch (e) {
      console.error('Error toggling monitor:', e);
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSchedulerLoading(false);
    }
  };

  // Handler for interval change
  const handleIntervalChange = async (val) => {
    setSchedulerLoading(true);
    try {
      const newInterval = parseInt(val);
      const result = await api.setXMonitorInterval(newInterval);
      console.log('Interval change result:', result);
      // Actualizar estado local inmediatamente
      setStats(prev => ({
        ...prev,
        monitoring_interval_minutes: newInterval,
        scheduler: {
          ...prev?.scheduler,
          interval_minutes: newInterval
        }
      }));
      // Refresh data después de un pequeño delay
      setTimeout(() => fetchData(), 500);
    } catch (e) {
      console.error('Error changing interval:', e);
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSchedulerLoading(false);
    }
  };

  // Fetch data - NON-BLOCKING with individual error handling
  const fetchData = useCallback(async () => {
    try {
      // Fetch in parallel with individual error handling
      const results = await Promise.allSettled([
        api.getPendingApprovals('PENDING', pageLimit, currentPage, activeFilters),
        api.getStories('ACTIVE'),
        api.getXMonitorStatus()
      ]);
      
      // Process approvals
      if (results[0].status === 'fulfilled') {
        setApprovals(results[0].value.approvals || []);
        setPagination({
          total_count: results[0].value.total_count || 0,
          total_pages: results[0].value.total_pages || 1,
          has_next: results[0].value.has_next || false,
          has_prev: results[0].value.has_prev || false
        });
      } else {
        console.error('Error fetching approvals:', results[0].reason);
      }
      
      // Process stories
      if (results[1].status === 'fulfilled') {
        setStories(results[1].value.stories || []);
      } else {
        console.error('Error fetching stories:', results[1].reason);
      }
      
      // Process status
      if (results[2].status === 'fulfilled') {
        setStats(results[2].value);
      } else {
        console.error('Error fetching status:', results[2].reason);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
    } finally {
      setLoading(false);
    }
  }, [pageLimit, currentPage, activeFilters]);

  useEffect(() => {
    fetchData();
    // Aumentar intervalo a 60 segundos para reducir carga
    const interval = setInterval(() => {
      // Solo refrescar si la pestaña está visible
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 60000); // 60 segundos en lugar de 30
    return () => clearInterval(interval);
  }, [fetchData]);
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  
  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setCurrentPage(1); // Reset to first page
    setSelectedIds([]); // Clear selection
  };
  
  // Multi-select handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === approvals.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(approvals.map(a => a.approval_id));
    }
  };
  
  const toggleSelect = (approvalId) => {
    setSelectedIds(prev => 
      prev.includes(approvalId) 
        ? prev.filter(id => id !== approvalId)
        : [...prev, approvalId]
    );
  };
  
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    const actionText = action === 'APPROVE' ? 'aprobar' : 'eliminar';
    const confirmed = window.confirm(
      `¿${action === 'APPROVE' ? 'Aprobar' : 'Eliminar'} ${selectedIds.length} tweets seleccionados?\n\n` +
      (action === 'APPROVE' 
        ? 'Se generarán artículos en segundo plano. NO esperes, sigue trabajando.'
        : 'Esta acción no se puede deshacer.')
    );
    
    if (!confirmed) return;
    
    // Optimistic update - remove items from list IMMEDIATELY
    const itemsToProcess = [...selectedIds];
    setApprovals(prev => prev.filter(a => !selectedIds.includes(a.approval_id)));
    setPagination(prev => ({
      ...prev,
      total_count: Math.max(0, prev.total_count - selectedIds.length)
    }));
    setSelectedIds([]);
    setBulkProcessing(false);
    
    // Show non-blocking feedback
    setRunResult({
      success: true,
      message: `⏳ Procesando ${itemsToProcess.length} tweets en segundo plano. Sigue trabajando, esto puede tomar varios minutos.`,
      details: null
    });
    setTimeout(() => setRunResult(null), 8000);
    
    // CRITICAL: Fire-and-forget - NO AWAIT
    api.bulkProcessApprovals(itemsToProcess, action)
      .then(result => {
        console.log('✅ Bulk process completado:', result);
        if (result.success) {
          setRunResult({
            success: true,
            message: `✅ Completado: ${result.results.success} exitosos, ${result.results.failed} fallidos`,
            details: null
          });
          setTimeout(() => setRunResult(null), 8000);
        }
        // NO refresh automático - usuario refresca manualmente
      })
      .catch(error => {
        console.error('Bulk action error:', error);
        // Solo refresh en error
        fetchData();
      });
  };

  // Run monitoring manually
  const handleRunMonitor = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const result = await api.runXMonitor();
      if (result.success) {
        setRunResult({
          success: true,
          message: `Encontrados ${result.results?.tweets_found || 0} tweets, ${result.results?.approvals_created || 0} nuevos pendientes`,
          details: result.results
        });
        await fetchData();
      } else {
        setRunResult({ success: false, message: 'Error al ejecutar' });
      }
    } catch (error) {
      console.error('Error running monitor:', error);
      setRunResult({ 
        success: false, 
        message: error.response?.data?.detail || error.message || 'Error de conexión'
      });
    } finally {
      setRunning(false);
    }
  };

  // Process approval - this can take several minutes
  // CRITICAL FIX: Fire-and-forget pattern - NO ESPERAR la respuesta
  const handleProcessApproval = async (approvalId, action, editedData = null) => {
    // Set ONLY this specific approval as processing
    setProcessingId(approvalId);
    
    // Optimistic UI update - remove from list IMMEDIATELY (non-blocking)
    setApprovals(prev => prev.filter(a => a.approval_id !== approvalId));
    setPagination(prev => ({
      ...prev,
      total_count: Math.max(0, prev.total_count - 1)
    }));
    setSelectedIds(prev => prev.filter(id => id !== approvalId));
    setSelectedApproval(null);
    setProcessingId(null);
    
    // Show feedback message
    if (action === 'APPROVE') {
      setRunResult({
        success: true,
        message: '⏳ Generando artículo en segundo plano (1-3 min). Puedes seguir trabajando. Revisa "Por Revisar" más tarde.',
        details: null
      });
      setTimeout(() => setRunResult(null), 8000);
    } else {
      setRunResult({
        success: true,
        message: '✅ Tweet eliminado.',
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
    }
    
    // CRITICAL: Fire-and-forget - NO AWAIT, no bloquea la UI
    const payload = { action };
    if (editedData) {
      payload.edited_title = editedData.title;
      payload.edited_body = editedData.body;
    }
    
    // Dispara la llamada pero NO espera la respuesta (fire-and-forget)
    // NO llamar fetchData() después para evitar bloqueo
    api.processApproval(approvalId, payload)
      .then(result => {
        console.log('✅ Proceso completado:', result);
        // NO refresh - usuario puede refrescar manualmente si quiere
      })
      .catch(error => {
        console.error('⚠️ Error en proceso:', error);
        // Solo refrescar si hay error para restaurar estado
        fetchData();
      });
  };

  // Account management
  const handleAddAccount = async () => {
    if (!newAccount.trim()) return;
    setAddingAccount(true);
    try {
      await api.addMonitoredAccount(newAccount.trim());
      setNewAccount('');
      await fetchData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAddingAccount(false);
    }
  };

  const handleRemoveAccount = async (account) => {
    if (!window.confirm(`¿Eliminar @${account} del monitoreo?`)) return;
    try {
      await api.removeMonitoredAccount(account);
      await fetchData();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Open approval detail
  const openApprovalDetail = (approval) => {
    setSelectedApproval(approval);
    setEditedTitle(approval.draft_title || '');
    setEditedBody(approval.draft_body || '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Twitter className="w-5 h-5" />
            Monitor X/Twitter
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {stats?.monitored_accounts?.length || 0} cuentas monitoreadas • Busca tweets de las últimas 24h
          </p>
        </div>
        
        <Button 
          onClick={handleRunMonitor}
          disabled={running}
          className="bg-black hover:bg-gray-800 h-12"
        >
          {running ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Buscando tweets...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Buscar Tweets Ahora
            </>
          )}
        </Button>
      </div>

      {/* Run Result Alert */}
      {runResult && (
        <div className={`p-4 rounded-xl border ${runResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-3">
            {runResult.success ? (
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={`font-medium ${runResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {runResult.message}
              </p>
              {runResult.details && (
                <p className="text-sm text-gray-600 mt-1">
                  Cuentas revisadas: {runResult.details.accounts_checked} | 
                  Tweets encontrados: {runResult.details.tweets_found} | 
                  Ya procesados: {runResult.details.tweets_processed - runResult.details.approvals_created}
                </p>
              )}
            </div>
            <button onClick={() => setRunResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Row */}
      {stats && (
      <>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.stats?.pending_approvals || 0}</p>
                <p className="text-xs text-gray-500">Por aprobar</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.stats?.active_stories || 0}</p>
                <p className="text-xs text-gray-500">Historias</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Twitter className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.stats?.total_tweets_ingested || 0}</p>
                <p className="text-xs text-gray-500">Tweets totales</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scheduler Controls */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stats.scheduler?.is_paused ? 'bg-red-500' : 'bg-green-500'} animate-pulse`}></div>
              <span className="text-sm font-medium">
                {stats.scheduler?.is_paused ? 'Scraping Pausado' : 'Scraping Activo'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Select 
                value={String(stats.scheduler?.interval_minutes || 15)}
                onValueChange={handleIntervalChange}
                disabled={schedulerLoading}
              >
                <SelectTrigger className="w-24 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(min => (
                    <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                size="sm"
                variant={stats.scheduler?.is_paused ? "default" : "outline"}
                className={stats.scheduler?.is_paused ? "bg-green-600 hover:bg-green-700" : ""}
                onClick={handleToggleMonitor}
                disabled={schedulerLoading}
              >
                {schedulerLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : stats.scheduler?.is_paused ? (
                  <>▶ Reanudar</>
                ) : (
                  <>⏸ Pausar</>
                )}
              </Button>
            </div>
          </div>
          {stats.scheduler?.next_run && !stats.scheduler?.is_paused && (
            <p className="text-xs text-gray-500 mt-2">
              Próximo scraping: {new Date(stats.scheduler.next_run).toLocaleTimeString()}
            </p>
          )}
        </div>
      </>
      )}

      {/* Tabs */}
      <div className="bg-gray-100 rounded-xl p-1 flex gap-1">
        <button
          onClick={() => setActiveView('approvals')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'approvals' 
              ? 'bg-white shadow-sm' 
              : 'text-gray-600 hover:bg-white/50'
          }`}
        >
          Por Aprobar ({pagination.total_count})
        </button>
        <button
          onClick={() => setActiveView('stories')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'stories' 
              ? 'bg-white shadow-sm' 
              : 'text-gray-600 hover:bg-white/50'
          }`}
        >
          Historias ({stories.length})
        </button>
        <button
          onClick={() => setActiveView('accounts')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeView === 'accounts' 
              ? 'bg-white shadow-sm' 
              : 'text-gray-600 hover:bg-white/50'
          }`}
        >
          Cuentas ({stats?.monitored_accounts?.length || 0})
        </button>
      </div>

      {/* Filter Button - Only show in approvals view */}
      {activeView === 'approvals' && (
        <Button
          onClick={() => setShowFilters(true)}
          variant="outline"
          className={`w-full py-3 rounded-xl ${filtersApplied ? 'bg-black text-white hover:bg-gray-800' : ''}`}
        >
          <Filter className="w-4 h-4 mr-2" />
          Filtros
          {filtersApplied && (
            <Badge className="ml-2 bg-white text-black">Activos</Badge>
          )}
        </Button>
      )}

      {/* Approvals View */}
      {activeView === 'approvals' && (
        <div className="space-y-4">
          {/* Pagination Controls */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Limit Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Mostrar:</span>
                <select
                  value={pageLimit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={0}>Todos</option>
                </select>
                <span className="text-sm text-gray-400">
                  de {pagination.total_count} tweets
                </span>
              </div>
              
              {/* Page Navigation */}
              {pageLimit > 0 && pagination.total_pages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    «
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.has_prev}
                  >
                    ‹
                  </Button>
                  
                  <span className="text-sm px-3">
                    Página <strong>{currentPage}</strong> de <strong>{pagination.total_pages}</strong>
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.has_next}
                  >
                    ›
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.total_pages)}
                    disabled={currentPage === pagination.total_pages}
                  >
                    »
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Multi-Select Actions Bar */}
          {approvals.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === approvals.length && approvals.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">
                      {selectedIds.length === approvals.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </span>
                  </label>
                  
                  {selectedIds.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {selectedIds.length} seleccionados
                    </Badge>
                  )}
                </div>
                
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleBulkAction('APPROVE')}
                      disabled={bulkProcessing}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {bulkProcessing ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Check className="w-4 h-4 mr-1" />
                      )}
                      Aprobar ({selectedIds.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkAction('REJECT')}
                      disabled={bulkProcessing}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar ({selectedIds.length})
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Approvals List */}
          {approvals.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Twitter className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No hay tweets pendientes</p>
              <p className="text-sm text-gray-400 mt-1">
                Haz click en "Buscar Tweets Ahora" para buscar nuevos
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {approvals.map((approval) => (
                <ApprovalCard 
                  key={approval.approval_id}
                  approval={approval}
                  onView={() => openApprovalDetail(approval)}
                  onApprove={() => handleProcessApproval(approval.approval_id, 'APPROVE')}
                  onReject={() => handleProcessApproval(approval.approval_id, 'REJECT')}
                  processing={processingId === approval.approval_id}
                  selected={selectedIds.includes(approval.approval_id)}
                  onToggleSelect={() => toggleSelect(approval.approval_id)}
                  bulkProcessing={bulkProcessing}
                />
              ))}
            </div>
          )}
          
          {/* Bottom Pagination */}
          {pageLimit > 0 && pagination.total_pages > 1 && approvals.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.has_prev}
                >
                  ← Anterior
                </Button>
                
                <span className="text-sm px-4">
                  Página {currentPage} de {pagination.total_pages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.has_next}
                >
                  Siguiente →
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stories View */}
      {activeView === 'stories' && (
        <div className="space-y-3">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Al aprobar un tweet, se crea automáticamente una noticia en la sección "Noticias" del panel.
            </p>
          </div>
          
          {stories.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay historias activas</p>
            </div>
          ) : (
            stories.map((story) => (
              <StoryCard key={story.story_id} story={story} />
            ))
          )}
        </div>
      )}

      {/* Accounts View */}
      {activeView === 'accounts' && (
        <AccountsManager 
          stats={stats}
          onAccountAdded={fetchData}
          onAccountRemoved={fetchData}
          onAutoPublishToggled={fetchData}
        />
      )}

      {/* Approval Detail Modal */}
      {selectedApproval && (
        <ApprovalDetailModal
          approval={selectedApproval}
          editedTitle={editedTitle}
          editedBody={editedBody}
          setEditedTitle={setEditedTitle}
          setEditedBody={setEditedBody}
          onClose={() => setSelectedApproval(null)}
          onApprove={() => handleProcessApproval(
            selectedApproval.approval_id, 
            'APPROVE',
            { title: editedTitle, body: editedBody }
          )}
          onReject={() => handleProcessApproval(selectedApproval.approval_id, 'REJECT')}
          processing={processingId === selectedApproval?.approval_id}
        />
      )}

      {/* Filters Modal */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Usuario */}
            <div>
              <label className="block text-sm font-medium mb-2">Usuario</label>
              <Select 
                value={filters.account} 
                onValueChange={(val) => setFilters({...filters, account: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los usuarios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {stats?.monitored_accounts?.map(acc => (
                    <SelectItem key={acc.username} value={acc.username}>
                      @{acc.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Desde</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Hasta</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="w-full"
                />
              </div>
            </div>

            {/* Score */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Score Mín.</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={filters.scoreMin}
                  onChange={(e) => setFilters({...filters, scoreMin: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Score Máx.</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="100"
                  value={filters.scoreMax}
                  onChange={(e) => setFilters({...filters, scoreMax: e.target.value})}
                />
              </div>
            </div>

            {/* Ordenar por */}
            <div>
              <label className="block text-sm font-medium mb-2">Ordenar por</label>
              <Select 
                value={filters.sortBy} 
                onValueChange={(val) => setFilters({...filters, sortBy: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Fecha</SelectItem>
                  <SelectItem value="newsworthiness_score">Score</SelectItem>
                  <SelectItem value="account">Usuario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Orden Asc/Desc */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={filters.sortOrder === 'desc' ? 'default' : 'outline'}
                className={`flex-1 ${filters.sortOrder === 'desc' ? 'bg-black text-white' : ''}`}
                onClick={() => setFilters({...filters, sortOrder: 'desc'})}
              >
                Desc
              </Button>
              <Button
                type="button"
                variant={filters.sortOrder === 'asc' ? 'default' : 'outline'}
                className={`flex-1 ${filters.sortOrder === 'asc' ? 'bg-black text-white' : ''}`}
                onClick={() => setFilters({...filters, sortOrder: 'asc'})}
              >
                Asc
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  account: 'all',
                  dateFrom: '',
                  dateTo: '',
                  scoreMin: '',
                  scoreMax: '',
                  sortBy: 'created_at',
                  sortOrder: 'desc'
                });
                setActiveFilters({});
                setFiltersApplied(false);
                setCurrentPage(1);
                setShowFilters(false);
                fetchData();
              }}
              className="w-full"
            >
              Limpiar Todo
            </Button>
            <Button
              onClick={() => {
                setActiveFilters({...filters});
                setFiltersApplied(
                  filters.account !== 'all' || 
                  filters.dateFrom !== '' || 
                  filters.dateTo !== '' || 
                  filters.scoreMin !== '' || 
                  filters.scoreMax !== '' ||
                  filters.sortBy !== 'created_at' ||
                  filters.sortOrder !== 'desc'
                );
                setCurrentPage(1);
                setShowFilters(false);
              }}
              className="w-full bg-black hover:bg-gray-800"
            >
              Aplicar Filtros
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// APPROVAL CARD
// ============================================
const ApprovalCard = ({ approval, onView, onApprove, onReject, processing, selected, onToggleSelect, bulkProcessing }) => {
  const analysis = approval.analysis || {};
  
  return (
    <div className={`bg-white rounded-xl border p-4 transition-colors ${
      selected ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
    }`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="pt-1">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            disabled={bulkProcessing}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className="text-xs">
              @{approval.tweet_author}
            </Badge>
            {approval.is_update && (
              <Badge className="bg-purple-100 text-purple-700 text-xs">
                Actualización
              </Badge>
            )}
            {analysis.is_breaking_news && (
              <Badge className="bg-red-100 text-red-700 text-xs">
                Breaking
              </Badge>
            )}
            <span className="text-xs text-gray-400 ml-auto">
              {new Date(approval.created_at).toLocaleString('es-MX', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          
          <h3 className="font-semibold text-sm mb-1 line-clamp-2">
            {approval.draft_title || 'Sin título'}
          </h3>
          
          <p className="text-xs text-gray-500 line-clamp-2 mb-2">
            {approval.tweet_text}
          </p>
          
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-green-500" />
              Noticia: {analysis.newsworthiness_score || 0}%
            </span>
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-orange-500" />
              Novedad: {analysis.novelty_score || 0}%
            </span>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="h-9 w-9 p-0"
            onClick={onView}
            title="Ver detalles"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            className="h-9 w-9 p-0 bg-green-600 hover:bg-green-700"
            onClick={onApprove}
            disabled={processing || bulkProcessing}
            title="Aprobar y publicar"
          >
            {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
            onClick={onReject}
            disabled={processing || bulkProcessing}
            title="Rechazar"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// STORY CARD
// ============================================
const StoryCard = ({ story }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">
              {story.topic_label || 'General'}
            </Badge>
            <span className="text-xs text-gray-400">
              {new Date(story.created_at).toLocaleString('es-MX', { 
                day: 'numeric', 
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          <h3 className="font-semibold mb-1">{story.title}</h3>
          <p className="text-sm text-gray-500 line-clamp-2">{story.summary}</p>
          {story.source_tweet_url && (
            <a 
              href={story.source_tweet_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 flex items-center gap-1 mt-2"
            >
              Ver tweet original <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// APPROVAL DETAIL MODAL
// ============================================
const ApprovalDetailModal = ({ 
  approval, 
  editedTitle, 
  editedBody, 
  setEditedTitle, 
  setEditedBody,
  onClose, 
  onApprove, 
  onReject,
  processing 
}) => {
  const analysis = approval.analysis || {};
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Twitter className="w-5 h-5" />
            Revisar y Editar
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Original Tweet */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">@{approval.tweet_author}</Badge>
              <span className="text-xs text-gray-400">
                {new Date(approval.tweet_created_at).toLocaleString('es-MX')}
              </span>
            </div>
            <p className="text-sm">{approval.tweet_text}</p>
            <a 
              href={approval.tweet_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 flex items-center gap-1 mt-2"
            >
              Ver en X <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          
          {/* Editable Content */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Título (sin emojis)</label>
              <Input 
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="Título de la noticia"
                className="font-semibold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contenido</label>
              <Textarea 
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                placeholder="Cuerpo de la noticia"
                rows={5}
              />
            </div>
          </div>
          
          {/* Info */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>⏱️ Proceso no bloqueante:</strong> Al aprobar, la generación del artículo se ejecutará en segundo plano (1-3 minutos). El modal se cerrará inmediatamente y <strong>podrás seguir trabajando</strong> mientras se genera. El artículo aparecerá en "Por Revisar" cuando esté listo.
            </p>
          </div>
        </div>
        
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={processing}>
            Cancelar
          </Button>
          <Button 
            variant="outline" 
            className="text-red-600 hover:bg-red-50"
            onClick={onReject}
            disabled={processing}
          >
            <X className="w-4 h-4 mr-2" />
            Rechazar
          </Button>
          <Button 
            className="bg-yellow-600 hover:bg-yellow-700"
            onClick={onApprove}
            disabled={processing}
          >
            {processing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Generando borrador...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Generar Borrador
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// ACCOUNTS MANAGER with Auto-Publish Toggle
// ============================================
const AccountsManager = ({ stats, onAccountAdded, onAccountRemoved, onAutoPublishToggled }) => {
  const [newAccount, setNewAccount] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  const [togglingAccount, setTogglingAccount] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  const handleAddAccount = async () => {
    if (!newAccount.trim()) return;
    setAddingAccount(true);
    try {
      await api.addMonitoredAccount(newAccount.trim());
      setNewAccount('');
      onAccountAdded();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAddingAccount(false);
    }
  };

  const handleRemoveAccount = async (username) => {
    if (!window.confirm(`¿Eliminar @${username} del monitoreo?`)) return;
    try {
      await api.removeMonitoredAccount(username);
      onAccountRemoved();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleAutoPublish = async (username, currentValue) => {
    const newValue = !currentValue;
    
    if (newValue) {
      const confirmed = window.confirm(
        `⚠️ ACTIVAR AUTO-PUBLICACIÓN para @${username}\n\n` +
        `Los tweets de esta cuenta se publicarán automáticamente sin revisión humana.\n\n` +
        `• Límite: 10 publicaciones cada 24 horas\n` +
        `• Al alcanzar el límite, se desactiva automáticamente\n` +
        `• Solo se publican tweets con puntuación de relevancia ≥ 50\n\n` +
        `¿Continuar?`
      );
      if (!confirmed) return;
    }
    
    setTogglingAccount(username);
    try {
      await api.toggleAutoPublish(username, newValue);
      onAutoPublishToggled();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTogglingAccount(null);
    }
  };

  const fetchActivityLog = async () => {
    setLoadingLog(true);
    try {
      const log = await api.getAutoPublishLog();
      setActivityLog(log || []);
    } catch (error) {
      console.error('Error fetching log:', error);
    } finally {
      setLoadingLog(false);
    }
  };

  const accounts = stats?.monitored_accounts || [];
  const autoPublishCounts = stats?.auto_publish_counts_24h || {};
  const limit = stats?.auto_publish_limit || 10;

  return (
    <div className="space-y-4">
      {/* Add account form */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold mb-3">Agregar Cuenta</h3>
        <div className="flex gap-2">
          <Input
            value={newAccount}
            onChange={(e) => setNewAccount(e.target.value)}
            placeholder="@usuario o nombre de usuario"
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && handleAddAccount()}
          />
          <Button 
            onClick={handleAddAccount} 
            disabled={addingAccount || !newAccount.trim()}
            className="bg-black"
          >
            {addingAccount ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Accounts list with Auto-Publish Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Cuentas Monitoreadas ({accounts.length})</h3>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { setShowLog(true); fetchActivityLog(); }}
          >
            <History className="w-4 h-4 mr-1" />
            Historial
          </Button>
        </div>
        
        <div className="space-y-3">
          {accounts.map((account) => {
            const username = typeof account === 'string' ? account : account.username;
            const autoPublish = typeof account === 'object' ? account.auto_publish : false;
            const count24h = autoPublishCounts[username] || 0;
            const isAtLimit = count24h >= limit;
            
            return (
              <div
                key={username}
                className={`p-4 rounded-lg border transition-colors ${
                  autoPublish ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <a
                      href={`https://x.com/${username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm font-medium hover:text-blue-600"
                    >
                      <Twitter className="w-4 h-4 text-blue-500" />
                      @{username}
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </a>
                    
                    {autoPublish && (
                      <Badge className="bg-green-100 text-green-700">
                        <Zap className="w-3 h-3 mr-1" />
                        Auto
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Auto-publish toggle */}
                    <button
                      onClick={() => handleToggleAutoPublish(username, autoPublish)}
                      disabled={togglingAccount === username}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        autoPublish 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                      title={autoPublish ? 'Desactivar auto-publicación' : 'Activar auto-publicación'}
                    >
                      {togglingAccount === username ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : autoPublish ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                      {autoPublish ? 'ON' : 'OFF'}
                    </button>
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRemoveAccount(username)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Auto-publish stats */}
                {autoPublish && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-700">
                        <Activity className="w-4 h-4 inline mr-1" />
                        Auto-publicaciones (24h)
                      </span>
                      <span className={`font-bold ${isAtLimit ? 'text-red-600' : 'text-green-700'}`}>
                        {count24h} / {limit}
                        {isAtLimit && ' (LÍMITE)'}
                      </span>
                    </div>
                    {isAtLimit && (
                      <p className="text-xs text-red-600 mt-1">
                        Se desactivará automáticamente hasta que pasen 24h
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {accounts.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <Twitter className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No hay cuentas monitoreadas</p>
            </div>
          )}
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-xs text-yellow-800">
            <strong>⚡ Auto-publicación:</strong> Cuando está activada, los tweets relevantes se convierten en artículos y se publican automáticamente sin revisión humana. Límite: {limit} publicaciones cada 24 horas por cuenta.
          </p>
        </div>
      </div>
      
      {/* Activity Log Modal */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Auto-Publicación
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingLog ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : activityLog.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No hay actividad registrada</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activityLog.map((log, i) => (
                  <div 
                    key={i} 
                    className={`p-3 rounded-lg text-sm ${
                      log.type === 'AUTO_PUBLISH' ? 'bg-green-50 border border-green-100' :
                      log.type === 'AUTO_DISABLED' ? 'bg-red-50 border border-red-100' :
                      'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={
                        log.type === 'AUTO_PUBLISH' ? 'bg-green-100 text-green-700' :
                        log.type === 'AUTO_DISABLED' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {log.type === 'AUTO_PUBLISH' && '🚀 Publicado'}
                        {log.type === 'AUTO_DISABLED' && '⛔ Desactivado'}
                        {log.type === 'TOGGLE' && (log.auto_publish ? '✅ Activado' : '❌ Desactivado')}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {new Date(log.timestamp).toLocaleString('es-MX')}
                      </span>
                    </div>
                    <p className="font-medium">@{log.account}</p>
                    {log.title && <p className="text-gray-600 text-xs mt-1">{log.title}</p>}
                    {log.reason && <p className="text-red-600 text-xs mt-1">{log.reason}</p>}
                    {log.changed_by && <p className="text-gray-400 text-xs">Por: {log.changed_by}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default XMonitorManager;
